// The dust sweep's timing and its grain grid. It's the old site's snapshot transition (from the
// 2026 version), just bigger and slower. No GPU in here, so the field and the tests both use it.

/** A grain's side, in pixels of the old site's capture, which ran at up to 1.25 device pixels
    per CSS pixel. (field.ts sizes it in device pixels.) */
export const DUST_CELL_SIZE = 4;
/** The most grains in one sweep. Past that, the grains get bigger until they fit. */
export const MAX_DUST_GRAINS = 180000;
/** The gust is the old site's, 30% bigger and 30% slower. The grains move just like before,
    but in a frame `DUST_SIZE` times smaller than the page and on a clock `DUST_SLOW` times
    slower, so the old physics still work. */
export const DUST_SIZE = 1.3;
export const DUST_SLOW = 1.3;
/** How long a sweep runs, in seconds, and the last stretch of it, when any grains still in
    place fade out. */
export const DUST_DURATION_SECONDS = 1.25 * DUST_SLOW;
export const DUST_DISSOLVE_SECONDS = (1 / 3) * DUST_SLOW;

/** The grains' opacity `elapsed` seconds into a sweep: 1, then down to 0 over the dissolve. */
export function dustFade(elapsed: number): number {
  const fadeStart = DUST_DURATION_SECONDS - DUST_DISSOLVE_SECONDS;
  if (elapsed <= fadeStart) return 1;
  if (elapsed >= DUST_DURATION_SECONDS) return 0;
  return 1 - (elapsed - fadeStart) / DUST_DISSOLVE_SECONDS;
}

/** The grain grid over a frame: each grain's center (0 to 1 across the frame), row by row. */
export interface DustGrid {
  cellSize: number;
  columns: number;
  rows: number;
  count: number;
  uv: Float32Array;
}

/** A grid of grains at least `minimumCellSize` pixels wide over a `width` x `height` frame, with
    at most `MAX_DUST_GRAINS` grains. Past that, the cells grow a pixel at a time. */
export function buildDustGrid(
  width: number,
  height: number,
  minimumCellSize = DUST_CELL_SIZE,
): DustGrid {
  let cellSize = Math.max(1, Math.ceil(minimumCellSize));
  let columns = Math.ceil(width / cellSize);
  let rows = Math.ceil(height / cellSize);
  while (columns * rows > MAX_DUST_GRAINS) {
    cellSize += 1;
    columns = Math.ceil(width / cellSize);
    rows = Math.ceil(height / cellSize);
  }

  const count = columns * rows;
  const uv = new Float32Array(count * 2);
  let offset = 0;
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      uv[offset] = Math.min(1, (column * cellSize + cellSize / 2) / width);
      uv[offset + 1] = Math.min(1, (row * cellSize + cellSize / 2) / height);
      offset += 2;
    }
  }
  return { cellSize, columns, rows, count, uv };
}
